import { JobTypes } from 'BMapsSrc/jobs/JobDefinition';
import { fetchPropertyGroupWithJobman } from 'BMapsSrc/jobs/JobProperties';
import { ensureArray } from 'BMapsSrc/util/js_utils';
import { PropApplicability } from 'BMapsModel/ScopedPropertyGroup';
import { ClassificationTypes, Directionality, preparePropertyGroupMetadata } from './properties_common';

const maplightProps = {
    ADMET_ames: {
        label: 'Ames Mutagenicity',
        description: 'Predicts whether a compound is likely to be mutagenic (DNA-damaging) in the Ames bacterial test.',
        classificationType: ClassificationTypes.BINARY,
        unit: 'Probability',
        directionality: Directionality.ZERO,
        range: '0.0 - 1.0',
        goodRange: '<0.5',
        r2: 0.865,
    },
    ADMET_bbb_martins: {
        label: 'Blood-Brain Barrier Permeability',
        description: 'Predicts whether a compound can cross the blood-brain barrier and potentially act on the central nervous system.',
        classificationType: ClassificationTypes.BINARY,
        unit: 'Probability',
        directionality: Directionality.ONEHUNDRED,
        range: '0.0 - 1.0',
        goodRange: '>0.5',
        r2: 0.92,
    },
    ADMET_bioavailability_ma: {
        label: 'Oral Bioavailability (F%)',
        description: 'Estimates the fraction of an orally administered drug that reaches systemic circulation.',
        classificationType: ClassificationTypes.BINARY,
        unit: 'Probability',
        directionality: Directionality.ONEHUNDRED,
        range: '0.0 - 1.0',
        goodRange: '>0.5',
        r2: 0.741,
    },
    ADMET_caco2_wang: {
        label: 'Caco-2 Permeability (Papp)',
        description: 'Predicts permeability across intestinal epithelial cells (Caco-2 assay proxy for oral absorption).',
        classificationType: ClassificationTypes.REGRESSION,
        unit: '10^-6 cm/s',
        directionality: Directionality.MAXIMIZE,
        range: '0.1 - 100',
        goodRange: '>10',
        r2: 0.276,
    },
    ADMET_clearance_hepatocyte_az: {
        label: 'Hepatocyte Clearance',
        description: 'Predicts hepatic clearance rates using hepatocyte data as a proxy for drug metabolism.',
        classificationType: ClassificationTypes.REGRESSION,
        unit: 'mL/min/kg',
        directionality: Directionality.NOT_APPLICABLE,
        range: '<13 (low), 13 - 31 (mod), >31 (high)',
        goodRange: '13 - 31',
        r2: 0.469,

    },
    ADMET_clearance_microsome_az: {
        label: 'Microsome Clearance',
        description: 'Predicts metabolic clearance based on liver microsomes containing CYP enzymes.',
        classificationType: ClassificationTypes.REGRESSION,
        unit: 'mL/min/kg',
        directionality: Directionality.NOT_APPLICABLE,
        range: '<13 (low), 13 - 31 (mod), >31 (high)',
        goodRange: '13 - 31',
        r2: 0.625,
    },
    ADMET_cyp2c9_substrate_carbonmangels: {
        label: 'CYP2C9 Substrate',
        description: 'Predicts whether the compound is metabolized by CYP2C9 enzyme.',
        classificationType: ClassificationTypes.BINARY,
        unit: 'Probability',
        directionality: Directionality.ZERO,
        range: '0.0 - 1.0',
        goodRange: '<0.5',
        r2: 0.411,
    },
    ADMET_cyp2c9_veith: {
        label: 'CYP2C9 Inhibition',
        description: 'Predicts inhibition of CYP2C9, relevant for drug-drug interaction risk.',
        classificationType: ClassificationTypes.BINARY,
        unit: 'Probability',
        directionality: Directionality.ZERO,
        range: '0.0 - 1.0',
        goodRange: '<0.5',
        r2: 0.786,
    },
    ADMET_cyp2d6_substrate_carbonmangels: {
        label: 'CYP2D6 Substrate',
        description: 'Predicts whether the compound is metabolized by CYP2D6 enzyme.',
        classificationType: ClassificationTypes.BINARY,
        unit: 'Probability',
        directionality: Directionality.ZERO,
        range: '0.0 - 1.0',
        goodRange: '<0.5',
        r2: 0.718,
    },
    ADMET_cyp2d6_veith: {
        label: 'CYP2D6 Inhibition',
        description: 'Predicts inhibition of CYP2D6 enzyme, which may cause drug-drug interactions.',
        classificationType: ClassificationTypes.BINARY,
        unit: 'Probability',
        directionality: Directionality.ZERO,
        range: '0.0 - 1.0',
        goodRange: '<0.5',
        r2: 0.72,
    },
    ADMET_cyp3a4_substrate_carbonmangels: {
        label: 'CYP3A4 Substrate',
        description: 'Predicts whether the compound is metabolized by CYP3A4, the most clinically relevant CYP enzyme.',
        classificationType: ClassificationTypes.BINARY,
        unit: 'Probability',
        directionality: Directionality.ZERO,
        range: '0.0 - 1.0',
        goodRange: '<0.5',
        r2: 0.649,
    },
    ADMET_cyp3a4_veith: {
        label: 'CYP3A4 Inhibition',
        description: 'Predicts inhibition of CYP3A4 activity, important for drug-drug interaction risk.',
        classificationType: ClassificationTypes.BINARY,
        unit: 'Probability',
        directionality: Directionality.ZERO,
        range: '0.0 - 1.0',
        goodRange: '<0.5',
        r2: 0.881,
    },
    ADMET_dili: {
        label: 'Drug-Induced Liver Injury Risk',
        description: 'Estimates the risk of hepatotoxicity (liver damage), a leading cause of drug withdrawal.',
        classificationType: ClassificationTypes.BINARY,
        unit: 'Probability',
        directionality: Directionality.ZERO,
        range: '0.0 - 1.0',
        goodRange: '<0.5',
        r2: 0.883,
    },
    ADMET_half_life_obach: {
        label: 'Half-Life',
        description: 'Estimates elimination half-life, an important determinant of dosing frequency.',
        classificationType: ClassificationTypes.REGRESSION,
        unit: 'hours',
        directionality: Directionality.NOT_APPLICABLE,
        range: '0.1 - 200',
        goodRange: '12 - 48',
        r2: 0.559,
    },
    ADMET_herg: {
        label: 'hERG Inhibition',
        description: 'Predicts the likelihood of blocking the cardiac hERG potassium channel, which can cause arrhythmia.',
        classificationType: ClassificationTypes.BINARY,
        unit: 'Probability',
        directionality: Directionality.ZERO,
        range: '0.0 - 1.0',
        goodRange: '<0.5',
        r2: 0.873,
    },
    ADMET_hia_hou: {
        label: 'Human Intestinal Absorption (HIA)',
        description: 'Classifies compounds as absorbed or not absorbed in the human gastrointestinal tract.',
        classificationType: ClassificationTypes.BINARY,
        unit: 'Probability',
        directionality: Directionality.ONEHUNDRED,
        range: '0.0 - 1.0',
        goodRange: '>0.5',
        r2: 0.983,
    },
    ADMET_ld50_zhu: {
        label: 'LD50',
        description: 'Predicts the median lethal dose (LD50) in rodents, a classic measure of acute toxicity.',
        classificationType: ClassificationTypes.REGRESSION,
        unit: 'mg/kg',
        directionality: Directionality.MAXIMIZE,
        range: '2 - 20000',
        goodRange: '>2000',
        r2: 0.618,
    },
    ADMET_lipophilicity_astrazeneca: {
        label: 'Lipophilicity ',
        description: 'Predicts lipophilicity (e.g., logP/logD), which influences membrane permeability, metabolism, and distribution.',
        classificationType: ClassificationTypes.REGRESSION,
        unit: 'logP',
        directionality: Directionality.NOT_APPLICABLE,
        range: '-1 - 6',
        goodRange: '1 - 3',
        r2: 0.542,

    },
    ADMET_pgp_broccatelli: {
        label: 'P-gp Inhibition',
        description: 'Predicts inhibition of P-glycoprotein activity. P-gp substrates can limit absorption and brain penetration.',
        classificationType: ClassificationTypes.BINARY,
        unit: 'Probability',
        directionality: Directionality.ONEHUNDRED,
        range: '0.0 - 1.0',
        goodRange: '>0.5',
        r2: 0.932,
    },
    ADMET_ppbr_az: {
        label: 'Plasma Protein Binding',
        description: 'Predicts the fraction of drug bound to plasma proteins versus free drug available for action.',
        classificationType: ClassificationTypes.REGRESSION,
        unit: '% bound',
        directionality: Directionality.ZERO,
        range: '0 - 100',
        goodRange: '<90',
        r2: 0.622,
    },
    ADMET_solubility_aqsoldb: {
        label: 'Aqueous solubility',
        description: 'Predicts intrinsic water solubility, a key property for formulation and absorption.',
        classificationType: ClassificationTypes.REGRESSION,
        unit: 'g/mL',
        directionality: Directionality.MAXIMIZE,
        range: '4 - 4000',
        goodRange: '>4',
        r2: 0.787,
    },
    ADMET_vdss_lombardo: {
        label: 'Volume of Distribution (VDss)',
        description: 'Predicts how extensively the drug distributes into tissues compared to plasma.',
        classificationType: ClassificationTypes.REGRESSION,
        unit: 'L/kg',
        directionality: Directionality.NOT_APPLICABLE,
        range: '<0.2 (low), 0.2 - 0.7 (mod), >0.7 (high)',
        goodRange: '0.2 - 0.7',
        r2: 0.716,
    },
};

export const maplightMetadata = preparePropertyGroupMetadata({
    groupMeta: {
        propApplicability: PropApplicability.PERCOMPOUND,
        label: 'MapLight ADMET properties',
    },
    propertyInfo: maplightProps,
    tooltipHeading: 'MapLight ADMET prediction',
});

/**
 * Request MapList properties for a group of compounds.
 * @param {Array<import('BMapsModel').Compound} compoundsIn
 * @param {{ trigger: function?, recalculate: boolean? }} options
 * @returns {Promise<void>}
 */
export async function getMapLightProperties(compoundsIn, { trigger, recalculate }={}) {
    const compounds = ensureArray(compoundsIn);
    const uniqueCmpds = new Map();
    for (const cmpd of compounds) {
        if (!recalculate && cmpd.scopedProperties.hasScope(JobTypes.MAPLIGHT)) {
            continue;
        }
        const uniqueStructId = cmpd.getUniqueStructId();
        const smiles = cmpd.getUnnamedSmiles();
        if (!uniqueCmpds.has(uniqueStructId)) {
            uniqueCmpds.set(cmpd.getUniqueStructId(), { cmpd, smiles });
        }
    }

    if (uniqueCmpds.size === 0) {
        return;
    }

    const maplightData = [...uniqueCmpds.entries()].reduce((acc, [uniqueStruct, { smiles }]) => (
        { ...acc, [uniqueStruct]: { smiles } }
    ), {});

    const jobRequestData = {
        jobtype: JobTypes.MAPLIGHT,
        data: maplightData,
        stash: { recalculate },
    };

    // getValues is provided as an example, but it is not used
    // because the job integrateResults already applies the properties to the compounds.
    function getValues(results, cmpd) {
        const structId = cmpd.getUniqueStructId();
        const values = results.jobResults[structId];
        if (!values) {
            console.error(`No MapLight properties returned for ${cmpd.resSpec}`);
            return null;
        }
        return Object.entries(values).reduce((acc, [propName, value]) => ({
            ...acc, [propName]: { value },
        }));
    }
    await fetchPropertyGroupWithJobman(
        compounds, JobTypes.MAPLIGHT, jobRequestData, { trigger }
    );
}
